/** Bookings grid "Edit bookings" batch dialog (`edit_fields`).
 *
 *  One block per field, each at most once; widgets, "next sync" badges and the
 *  live cache/sync preview come from the backend catalog. The operator must
 *  choose explicitly whether update events fire, and — when relevant — how a
 *  cancellation runs, what happens to booking-specific ends when the start moves,
 *  to the old tour's own tag categories, and to the order link. A reason is
 *  mandatory. The dry run is optional: it runs the same planner as the apply and
 *  is reviewed inline (editing anything voids it); Apply asks for confirmation
 *  and runs in the background.
 *
 *  Plan: backend aidocs/plans/2026-09-30-console-booking-batch-edit.md.
 */
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { runBatch } from '@/api/batch';
import type { BookingEditTaskResult } from '@/api/bookingEdit';
import { fetchBookingEditCatalog } from '@/api/bookingEdit';
import { ConsoleApiError } from '@/api/client';
import { AppDialog } from '@/components/AppDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';
import { useBatchTaskResult } from '@/features/batch-edit/useBatchTaskResult';
import { openBackgroundTask } from '@/features/bgtasks/openBackgroundTask';

import { BookingEditReport } from './BookingEditReport';
import { ChoiceField, FieldBlock } from './FieldBlock';
import {
  activeBlocks,
  buildParams,
  newBlock,
  operationIdentity,
  previewEffects,
  requirements,
  submitBlocker,
  withField,
  type BlockDraft,
  type RunOptions,
} from './model';

const EMPTY_OPTS: RunOptions = { updateEvents: null, customEnd: null, oldTourTags: null, orderLink: null };

export function EditBookingsDialog({ ctx }: { ctx: BatchActionContext }) {
  const snackbar = useSnackbar();
  const [blocks, setBlocks] = useState<BlockDraft[]>(() => [newBlock()]);
  const [opts, setOpts] = useState<RunOptions>(EMPTY_OPTS);
  const [reason, setReason] = useState('');
  const [dryRunTaskId, setDryRunTaskId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const catalogQuery = useQuery({
    queryKey: ['booking-edit-catalog'],
    queryFn: fetchBookingEditCatalog,
    staleTime: 5 * 60_000,
  });
  const catalog = catalogQuery.data;

  const identity = useMemo(
    () => operationIdentity(catalog, blocks, opts, ctx.selection),
    [catalog, blocks, opts, ctx.selection],
  );
  useEffect(() => {
    setDryRunTaskId(null);
  }, [identity]);
  const dryRun = useBatchTaskResult<BookingEditTaskResult>(dryRunTaskId, 'booking-edit');

  const req = requirements(blocks);
  const blocker = catalog ? submitBlocker(catalog, blocks, opts, reason) : 'Loading…';
  const preview = catalog ? previewEffects(catalog, blocks) : { flushed: [], automatic: [] };
  const allFieldsUsed = catalog != null && blocks.filter((b) => b.field).length >= catalog.fields.length;

  const tourTenant = blocks.find((b) => b.field === 'tour')?.tour?.tenant?.id ?? null;
  const roleBlock = blocks.find((b) => b.field === 'role');
  const roleTenant = roleBlock && !roleBlock.roleClear ? (roleBlock.role?.tenant?.id ?? null) : null;
  const pickerTenantFor = (b: BlockDraft) =>
    ctx.tenantFilterId ?? (b.field === 'tour' ? roleTenant : b.field === 'role' ? tourTenant : null);

  const errorText = (e: unknown) =>
    e instanceof ConsoleApiError
      ? ((e.body as { detail?: string } | undefined)?.detail ?? e.message)
      : (e as Error).message;

  const dispatch = (dryRunFlag: boolean) =>
    runBatch(
      'bookings',
      'edit_fields',
      ctx.selection,
      buildParams(catalog!, blocks, opts, reason, dryRunFlag) as unknown as Record<string, unknown>,
      ctx.filterQuery,
    );

  const dryRunMut = useMutation({
    mutationFn: () => dispatch(true),
    onSuccess: (r) => {
      setDryRunTaskId(r.task_id);
      if (r.capped) {
        snackbar.show(
          `Selection capped at ${r.processed.toLocaleString()} of ${r.matched.toLocaleString()} bookings.`,
          'warning',
        );
      }
    },
    onError: (e) => snackbar.show(errorText(e), 'error'),
  });

  const applyMut = useMutation({
    mutationFn: () => dispatch(false),
    onSuccess: (r) => {
      snackbar.show(
        `Editing ${r.processed.toLocaleString()} booking(s) in the background.`,
        'success',
        { autoHideDuration: 15000, action: { label: 'View task', onClick: () => openBackgroundTask(r.task_id) } },
      );
      ctx.clearSelection();
      ctx.close();
    },
    onError: (e) => {
      setConfirmOpen(false);
      snackbar.show(errorText(e), 'error');
    },
  });

  const busy = dryRunMut.isPending || applyMut.isPending;
  const patchBlock = (id: string, patch: Partial<BlockDraft>) =>
    setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const setField = (id: string, field: string) =>
    setBlocks((bs) => bs.map((b) => (b.id === id && catalog ? withField(b, field, catalog) : b)));
  const patchOpts = (patch: Partial<RunOptions>) => setOpts((o) => ({ ...o, ...patch }));

  const result = dryRun.result;
  const wouldChange = result?.counts.would_change ?? 0;
  const activeCount = activeBlocks(blocks).length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AppDialog
        open
        size="lg"
        onClose={busy ? () => undefined : ctx.close}
        disableBackdropClose
        title={
          <>
            Edit {ctx.count.toLocaleString()} booking(s) <HelpLink story="booking-batch-edit" />
          </>
        }
        actions={
          <>
            <Button onClick={ctx.close} disabled={busy}>
              Cancel
            </Button>
            <Tooltip title={blocker ?? ''}>
              <span>
                <Button
                  variant="outlined"
                  onClick={() => dryRunMut.mutate()}
                  disabled={busy || blocker != null || dryRun.waitingForTask || dryRun.waitingForResult}
                >
                  Dry run
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={blocker ?? ''}>
              <span>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => setConfirmOpen(true)}
                  disabled={busy || blocker != null}
                >
                  Apply…
                </Button>
              </span>
            </Tooltip>
          </>
        }
      >
        <Stack spacing={2}>
          {catalogQuery.isLoading && <LinearProgress />}
          {catalogQuery.isError && (
            <Alert severity="error">The booking edit catalog failed to load — try reopening the dialog.</Alert>
          )}
          {catalog && !catalog.enabled && (
            <Alert severity="error">Booking batch edit is disabled in this environment (ConsoleBookingEditEnabled).</Alert>
          )}

          {catalog && (
            <>
              <Typography variant="body2" color="text.secondary">
                Add one block per field to change. Dates and times are wall-clock values read in each booking’s
                own tour timezone. Most fields are owned by the booking’s integration — the badge on each block says
                whether the next sync keeps or reverts the edit.
              </Typography>

              <Stack spacing={1.5}>
                {blocks.map((b, i) => (
                  <FieldBlock
                    key={b.id}
                    index={i}
                    block={b}
                    blocks={blocks}
                    catalog={catalog}
                    ctx={ctx}
                    opts={opts}
                    onOptsChange={patchOpts}
                    pickerTenantId={pickerTenantFor(b)}
                    canRemove={blocks.length > 1}
                    onFieldChange={(field) => setField(b.id, field)}
                    onChange={(patch) => patchBlock(b.id, patch)}
                    onRemove={() => setBlocks((bs) => bs.filter((x) => x.id !== b.id))}
                  />
                ))}
              </Stack>
              <Box>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  disabled={allFieldsUsed}
                  onClick={() => setBlocks((bs) => [...bs, newBlock()])}
                >
                  Add field
                </Button>
              </Box>

              <ChoiceField
                label="Update events (tour_participant.updated / date_changed)"
                value={opts.updateEvents}
                onChange={(v) => patchOpts({ updateEvents: v as RunOptions['updateEvents'] })}
                options={[
                  { value: 'emit', label: 'Emit — notifications configured for these changes may go out' },
                  { value: 'suppress', label: 'Suppress — no update events for this run' },
                ]}
              />
              {req.customEnd && (
                <ChoiceField
                  label="Bookings with a booking-specific end datetime, when their start moves"
                  value={opts.customEnd}
                  onChange={(v) => patchOpts({ customEnd: v as RunOptions['customEnd'] })}
                  options={[
                    { value: 'move', label: 'Move the end by the same amount' },
                    { value: 'keep', label: 'Keep the end unchanged (bookings whose end would precede the start are rejected)' },
                  ]}
                />
              )}

              {ctx.selection.mode === 'all_matching' && (
                <Alert severity="info">
                  Acting on {ctx.matchedPhrase} bookings (at most {catalog.max_selection.toLocaleString()} per run).
                </Alert>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary" component="div">
                  Caches & syncs this run will trigger{result ? ' (exact counts in the dry run below)' : ''}:
                  {activeCount === 0 && ' —'}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {preview.flushed.map((e) => (
                    <Tooltip key={e.code} title={e.description}>
                      <Chip size="small" label={e.label} />
                    </Tooltip>
                  ))}
                  {preview.automatic.map((e) => (
                    <Tooltip key={e.code} title={e.description}>
                      <Chip size="small" variant="outlined" label={`${e.label} (automatic)`} />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>

              <TextField
                label="Why are you making this change?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
                required
                error={reason.length > 0 && reason.trim().length < catalog.min_reason}
                helperText={
                  reason.length > 0 && reason.trim().length < catalog.min_reason
                    ? `At least ${catalog.min_reason} characters.`
                    : 'Recorded in the audit log and in each booking’s change history.'
                }
              />

              {dryRun.waitingForTask && (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip size="small" color="info" label="Dry run in progress" />
                    <Typography variant="caption" color="text.secondary">
                      Planning every selected booking without writing…
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant={dryRun.progressPct != null ? 'determinate' : 'indeterminate'}
                    value={dryRun.progressPct ?? 0}
                  />
                </Box>
              )}
              {dryRun.waitingForResult && <LinearProgress />}
              {dryRun.finished && !dryRun.succeeded && (
                <Alert severity="error">The dry run did not complete — check it in Background Tasks.</Alert>
              )}
              {result && dryRunTaskId && (
                <Box>
                  <Alert severity={wouldChange > 0 ? 'success' : 'warning'} sx={{ mb: 1 }}>
                    <AlertTitle>
                      {wouldChange > 0
                        ? `Dry run — ${wouldChange.toLocaleString()} booking(s) would change`
                        : 'Dry run — nothing would change'}
                    </AlertTitle>
                    Editing any block or option voids this preview. Apply plans every booking again as it is at that
                    moment.
                  </Alert>
                  <BookingEditReport result={result} taskId={dryRunTaskId} />
                </Box>
              )}
            </>
          )}
        </Stack>
      </AppDialog>
      <ConfirmDialog
        open={confirmOpen}
        title="Apply booking edits?"
        message={
          `Change ${activeCount} field(s) on ${ctx.count.toLocaleString()} booking(s)` +
          `${opts.updateEvents === 'emit' ? ', emitting update events' : ', with update events suppressed'}. ` +
          (result ? '' : 'No dry run was reviewed. ') +
          'This runs in the background and is written to the audit log.'
        }
        confirmLabel="Apply"
        destructive
        onConfirm={() => applyMut.mutate()}
        onCancel={() => setConfirmOpen(false)}
      />
    </LocalizationProvider>
  );
}
